import React, { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile on the signup form. The token it yields is sent as
 * Cognito SignUp ValidationData and verified by the pre-signup Lambda (deploy
 * repo, cfn/06-cognito.yml) — the check itself is server-side, so a bot calling
 * Cognito directly can't skip it. Renders nothing until
 * REACT_APP_TURNSTILE_SITE_KEY is set, so the build is safe to ship first.
 */
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render(
    el: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    }
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptLoad: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptLoad) {
    scriptLoad = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptLoad = null; // let a later mount retry
        reject(new Error("Turnstile failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptLoad;
}

interface Props {
  /** Receives a fresh token, or null when it expires, errors or is reset.
   *  Must be stable (a state setter is) — it is an effect dependency. */
  onToken: (token: string | null) => void;
  /** Bump to get a new token: Cloudflare tokens are single-use, so a failed
   *  signup attempt has spent the current one. */
  resetKey: number;
}

export default function TurnstileWidget({ onToken, resetKey }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let widgetId: string | undefined;
    let cancelled = false;
    onToken(null);
    loadTurnstile()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return;
        setUnavailable(false);
        widgetId = window.turnstile.render(container.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "signup",
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken, resetKey]);

  if (!TURNSTILE_SITE_KEY) return null;
  return (
    <div className="form-group">
      <div ref={container} />
      {unavailable && (
        <small className="form-text text-danger">
          The verification check couldn't load. A content blocker may be stopping
          it — allow challenges.cloudflare.com on this page, or try another browser.
        </small>
      )}
    </div>
  );
}
